/**
 * Floaty Core Data Types
 * Generated by Design OS
 */

// Re-export series types
export * from './series'

// ============================================
// Core Entities
// ============================================

export interface User {
  id: string
  name: string
  email: string
  avatarUrl?: string
  phone?: string
  location?: string
  bio?: string
  socialLinks?: {
    twitter?: string
    linkedin?: string
    website?: string
  }
}

export interface Team {
  id: string
  name: string
  logoUrl?: string
}

export type Role = 'owner' | 'admin' | 'member'

export interface Membership {
  id: string
  userId: string
  teamId: string
  role: Role
  joinedAt: string
}

export interface TeamMember extends User {
  role: Role
  joinedAt?: string
}

export interface PendingInvite {
  id: string
  email: string
  role: 'admin' | 'member'
  sentAt: string
}

// ============================================
// Pipeline & Stages
// ============================================

export type StageId = 'invited' | 'responded' | 'prep' | 'recorded' | 'published'

export interface Stage {
  id: StageId
  label: string
}

export const STAGES: Stage[] = [
  { id: 'invited', label: 'Invited' },
  { id: 'responded', label: 'Responded' },
  { id: 'prep', label: 'Prep' },
  { id: 'recorded', label: 'Recorded' },
  { id: 'published', label: 'Published' },
]

export type DeadlineStatus = 'at-risk' | 'tight' | 'ready' | 'normal' | 'done' | 'declined'

export interface PipelineGuest {
  id: string
  name: string
  title: string
  avatarUrl: string
  episode: string
  episodeNumber: number
  recordingDate: string
  airDate: string
  stage: StageId
  note?: string
  readyToRecord?: boolean
  declined?: boolean
}

// ============================================
// Episodes & Recordings
// ============================================

export type EpisodeStatus = 'scheduled' | 'recorded' | 'in-production' | 'published' | 'cancelled'

export interface Episode {
  id: string
  series_id: string
  number: number
  title: string
  description?: string
  guestIds: string[]
  guestNames: string[]
  recordingDate: string
  airDate: string
  status: EpisodeStatus
  productionLeadDays: number
  thumbnailUrl?: string
  duration?: string
  listens?: number
  notes?: string
}

export interface Guest {
  id: string
  name: string
  title: string
  company?: string
  avatarUrl: string
  email?: string
}

export interface Recording {
  id: string
  episodeNumber: number
  title: string
  description?: string
  guests: Guest[]
  recordingDate: string
  recordingTime?: string
  airDate: string
  stage: StageId
  platform?: string
  meetingLink?: string
  duration?: string
}

export interface PrepChecklist {
  researchComplete: boolean
  questionsReady: boolean
  guestBriefed: boolean
  equipmentTested: boolean
}

export interface PostProduction {
  editing: 'not-started' | 'in-progress' | 'complete'
  transcription: 'not-started' | 'in-progress' | 'complete'
  showNotes: 'not-started' | 'in-progress' | 'complete'
  artwork: 'not-started' | 'in-progress' | 'complete'
}

export interface Activity {
  id: string
  action: string
  timestamp: string
}

export interface RecordingDetail {
  episode: {
    id: string
    number: number
    title: string
    description?: string
    airDate: string
    recordingDate: string
    recordingTime?: string
    platform?: string
    meetingLink?: string
    duration?: string
    stage: StageId
  }
  guests: Guest[]
  prep: PrepChecklist
  activity: Activity[]
  notes?: string
  postProduction?: PostProduction
}

// ============================================
// Guest Profiles
// ============================================

export interface GuestProfile {
  id: string
  name: string
  title: string
  company?: string
  avatarUrl: string
  email?: string
  phone?: string
  location?: string
  bio?: string
  socialLinks?: {
    twitter?: string
    linkedin?: string
    website?: string
  }
  tags?: string[]
  appearanceCount: number
  lastAppearance?: string
  status?: 'active' | 'archived'
}

export interface Appearance {
  id: string
  episodeNumber: number
  episodeTitle: string
  recordingDate: string
  airDate: string
  stage: StageId
}

export interface Communication {
  id: string
  type: 'email' | 'note' | 'call'
  subject?: string
  content: string
  date: string
  direction?: 'inbound' | 'outbound'
}

export interface GuestProfileDetail {
  guest: GuestProfile
  appearances: Appearance[]
  communications: Communication[]
  notes?: string
}

// ============================================
// Scheduling & Calendar
// ============================================

export type EventType = 'recording' | 'air-date' | 'deadline'

export interface CalendarEvent {
  id: string
  episodeId: string
  type: EventType
  date: string
  title: string
  episodeNumber: number
  guestNames: string[]
}

export interface CalendarDay {
  date: string
  isCurrentMonth: boolean
  isToday: boolean
  events: CalendarEvent[]
}

export interface ProductionSettings {
  defaultLeadDays: number
  recordingBufferDays: number
  maxEpisodesPerDay: number
}

// ============================================
// Integrations
// ============================================

export type IntegrationCategory = 'calendar' | 'recording' | 'communication'

export type ConnectionStatus = 'connected' | 'disconnected' | 'error' | 'syncing'

export type SyncFrequency = 'realtime' | 'hourly' | 'daily'

export interface Integration {
  id: string
  name: string
  description: string
  category: IntegrationCategory
  logoUrl: string
  status: ConnectionStatus
  isAvailable: boolean
  isPremium: boolean
  lastSynced?: string
  syncFrequency?: SyncFrequency
  connectedAt?: string
  errorMessage?: string
}

export interface IntegrationConfig {
  integrationId: string
  syncFrequency: SyncFrequency
  syncCalendars: boolean
  syncRecordings: boolean
  autoCreateEvents: boolean
  notifyOnSync: boolean
}

export interface IntegrationStats {
  eventsImported: number
  recordingsLinked: number
  lastSync: string
  nextSync: string
}

// ============================================
// Terminology / Custom Labels
// ============================================

export interface AppLabels {
  pipeline: string       // Always "Flow" (not customizable)
  collections: string    // Default: "Podcasts"
  recordings: string     // Default: "Episodes"
  guests: string         // Default: "Guests"
  schedule: string       // Default: "Calendar"
  appearances: string    // Default: "Bookings"
}

export const DEFAULT_LABELS: AppLabels = {
  pipeline: 'Flow',
  collections: 'Podcasts',
  recordings: 'Episodes',
  guests: 'Guests',
  schedule: 'Calendar',
  appearances: 'Bookings',
}

/** Available options for each customizable term */
export const TERMINOLOGY_OPTIONS: Record<keyof Omit<AppLabels, 'pipeline'>, string[]> = {
  collections: ['Podcasts', 'Shows', 'Series', 'Channels'],
  recordings: ['Episodes', 'Recordings', 'Sessions'],
  guests: ['Guests', 'Interviewees', 'Speakers', 'Talent'],
  schedule: ['Calendar', 'Schedule', 'Timeline'],
  appearances: ['Bookings', 'Appearances', 'Segments'],
}

// ============================================
// Billing & Subscriptions
// ============================================

export interface Plan {
  id: string
  name: string
  price: number
  priceYearly?: number
  description: string
  features: string[]
  highlighted?: boolean
  cta: string
}

export interface Subscription {
  id: string
  planId: string
  planName: string
  price: number
  billingCycle: 'monthly' | 'yearly'
  nextBillingDate: string
  status: 'active' | 'trialing' | 'past_due' | 'canceled'
  trialEndsAt?: string
}

export interface PaymentMethod {
  id: string
  type: 'card'
  last4: string
  brand: string
  expiresAt: string
}

export interface Invoice {
  id: string
  date: string
  amount: number
  status: 'paid' | 'pending' | 'failed'
  downloadUrl?: string
}

// ============================================
// Guest Prep (Magic Link)
// ============================================

export interface GuestPrepMaterials {
  topicOverview: string
  suggestedQuestions: string[]
  technicalRequirements: {
    camera: string
    microphone: string
    environment: string
    internet: string
  }
}

export interface GuestPrepEpisode {
  id: string
  title: string
  recordingDate: string
  recordingTime: string
  hostName: string
  hostAvatarUrl?: string
}

export interface GuestPrepData {
  guest: {
    id: string
    name: string
    email: string
  }
  episode: GuestPrepEpisode
  prepMaterials: GuestPrepMaterials
  meetingLink: string
  hostEmail: string
}
